import { useState } from "react";
import type { GameDetail, PublicPlayer, RoundView } from "backend/types";
import { api } from "./api";
import { Avatar, usePoll } from "./ui";
import { Leaderboard } from "./Leaderboard";

export function RoundEnd({
  round,
  game,
  me,
}: {
  round: RoundView;
  game: GameDetail;
  me: PublicPlayer;
}) {
  const { data, refresh } = usePoll(() => api.getGuess(round.roundId), 2500, [round.roundId]);
  const [busy, setBusy] = useState(false);
  const g = data?.guess;

  async function submit(playerId: string) {
    setBusy(true);
    try {
      await api.submitGuess(round.roundId, { guessedPlayerId: playerId });
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  if (!g) return <div className="p-6 text-ink/40">Loading…</div>;

  // Not guessed yet → show the picker.
  if (g.myGuessId == null) {
    return (
      <div className="mx-auto w-full max-w-md p-5 space-y-4">
        <div className="text-center">
          <div className="font-display text-xl font-bold">⏱ Round {round.index} over</div>
          <p className="text-sm text-ink/50 mt-1">Who do you think you were just chatting with?</p>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {g.candidates.map((c) => (
            <button
              key={c.id}
              disabled={busy}
              onClick={() => submit(c.id)}
              className="bg-white rounded-2xl border border-black/5 shadow-sm hover:shadow hover:border-wa/40 p-4 flex flex-col items-center gap-2 disabled:opacity-50 transition"
            >
              <Avatar color={c.color} size={44} />
              <span className="text-sm truncate w-full text-center">{c.name}</span>
            </button>
          ))}
        </div>
        <p className="text-center text-[11px] text-ink/40">just a fun guess — it's the engagement bit 🙂</p>
      </div>
    );
  }

  // Guessed → reveal + leaderboard.
  const partner = g.partner as PublicPlayer | null;
  return (
    <div className="mx-auto w-full max-w-md p-5 space-y-4">
      <div
        className={
          "rounded-2xl p-5 text-center shadow-sm border " +
          (g.correct ? "bg-wa/10 border-wa/30" : "bg-spice/10 border-spice/30")
        }
      >
        <div className="text-3xl">{g.correct ? "✅" : "❌"}</div>
        <div className="font-display text-lg font-bold mt-1">
          {g.correct ? "Correct!" : "Not quite."}
        </div>
        {partner && (
          <div className="flex items-center justify-center gap-2 mt-2 text-sm text-ink/70">
            <span>It was</span>
            <Avatar color={partner.color} size={22} />
            <span className="font-semibold">{partner.name}</span>
          </div>
        )}
      </div>

      <Leaderboard gameId={game.id} highlightId={me.id} />

      <p className="text-center text-sm text-ink/40">
        {game.status === "ended"
          ? "🏁 That's the end of the game!"
          : "⏳ waiting for the host to start the next round…"}
      </p>
    </div>
  );
}
